<script lang="ts">
	import { onMount, onDestroy } from "svelte";
	import Hydrator from "../../Hydrator.svelte";
	import IntersectionObserver from "../../IntersectionObserver.svelte";
	import Data from "./Data";

	import DomainView from "../../DomainView.svelte";
	export let type: string;
	export let data: z.infer<typeof Data>;
	export let id: string;

	// admin mode sends context this way, default to getContext when in renderer mode, pass context bellow
	import { getContext, setContext } from "svelte";
	import { z } from "zod";
	export let context = getContext("context");
	setContext("context", context);

	import {
		TWITFRAME_ORIGIN,
		isMeasurableService,
		embedHeightFromMessage,
	} from "./measure.js";

	let intersecting: any;
	let element: HTMLIFrameElement;

	// twitter/x (twitframe) and instagram report their real height only after
	// load, so those iframes must load well before entering the viewport or
	// the height change is CLS
	const earlyLoad = isMeasurableService(data.service);

	let aspectRatio = 1;
	if (data.width && data.height) {
		aspectRatio = data.width / data.height;
	}

	// height measured against the real embed in the admin editor — reserve it
	// on SSR so even above-the-fold embeds get (almost) the right box upfront
	let reservedHeight: number | undefined;
	if (
		earlyLoad &&
		typeof data.measured_height === "number" &&
		data.measured_height > 0
	) {
		reservedHeight = Math.round(data.measured_height);
	}

	function onLoadIframe() {
		// a src-less iframe fires a load event for about:blank right after
		// insertion, before bind:this ran — only query once twitframe is in
		// (instagram posts its MEASURE messages unprompted)
		if (
			(data.service === "twitter" || data.service === "x") &&
			element?.contentWindow &&
			element.src
		) {
			element.contentWindow.postMessage(
				{ element: id, query: "height" },
				TWITFRAME_ORIGIN,
			);
		}
	}

	function onMessage(e: MessageEvent) {
		const height = embedHeightFromMessage(e, element, id);
		if (height && element) {
			// min-height too: the live height must win over the SSR
			// reserve (measured_height) in both directions
			element.style.height = height + "px";
			element.style.minHeight = height + "px";
		}
	}

	// !element.src, NOT src != data.embed: the browser normalizes the URL on
	// readback (inserts "/" before "?") so equality never holds, and the src
	// assignment itself mutates `element` state and re-runs this statement —
	// together that's an infinite reload loop (legacy-mode dom-ref pitfall)
	$: if (earlyLoad && intersecting && element && !element.src) {
		element.src = data.embed;
	}

	onMount(() => {
		window.addEventListener("message", onMessage);
	});

	onDestroy(() => {
		// onDestroy also runs during SSR, where window does not exist
		if (typeof window !== "undefined") {
			window.removeEventListener("message", onMessage);
		}
	});
</script>

<!-- svelte-ignore missing-declaration -->
<Hydrator props={$$props} component="EditorEmbedViewDefault" hydrate={true}>
	<DomainView {type} {data} category="editorBlocks">
		<IntersectionObserver
			{element}
			bind:intersecting
			rootMargin="500vh"
			threshold={0.01}
		>
			<div class="container-embed">
				<iframe
					src={earlyLoad ? undefined : data.embed}
					on:load={onLoadIframe}
					style="--aspectRatio: {aspectRatio};{reservedHeight
						? ` min-height: ${reservedHeight}px;`
						: ''}"
					bind:this={element}
					class="embed-{data.service}"
					height={data.service !== "youtube" ? data.height : undefined}
					width={data.width}
					allowfullscreen={true}
					title="embed media"
					loading={earlyLoad ? undefined : "lazy"}
				/>
			</div>
		</IntersectionObserver>
	</DomainView>
</Hydrator>

<style lang="scss">
	.container-embed {
		display: flex;
		justify-content: center;
		align-items: center;

		iframe {
			width: calc(100% - 20px);
			margin: 10px;
			min-height: 250px;
			height: auto;
			aspect-ratio: var(--aspectRatio);
			border: none;
		}

		iframe.embed-twitter {
			max-width: 500px;
			margin: 10px auto 10px auto;
		}

		iframe.embed-x {
			max-width: 500px;
			margin: 10px auto 10px auto;
		}

		iframe.embed-youtube {
			aspect-ratio: 16/9;
			height: auto;
		}

		iframe.embed-tiktok {
			aspect-ratio: 9/16;
			max-height: 750px;
		}

		iframe.embed-instagram {
			max-width: 390px;
		}
	}
</style>
